/**
 * Static (SSR) mobile bottom nav for the SRP shell.
 * Renders the same layout as MobileBottomNav (search placeholder, All Filters button, menu)
 * so the initial paint on mobile shows full chrome; no interactivity (no Autocomplete, no modals).
 */
import { Menu } from 'lucide-react';
import { FunnelIcon as FunnelIconOutline } from '@heroicons/react/24/outline';
import type { SrpStaticStrings } from '../../../features/srp/SrpViewModel';

const iosFontStack =
  "font-[system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif]";

export interface StaticMobileBottomNavProps {
  staticStrings: SrpStaticStrings;
}

/**
 * Renders a non-interactive mobile bottom bar that matches MobileBottomNav layout and styling.
 * Used in the static SRP shell so mobile viewports see the full nav on first paint.
 */
export function StaticMobileBottomNav({ staticStrings }: StaticMobileBottomNavProps): React.ReactElement {
  return (
    <nav
      className={`w-full z-[60] flex items-center gap-2 px-3 py-2 border-t border-black/10 dark:border-gray-700 lg:hidden bg-white/95 dark:bg-gray-800/95 shadow-lg rounded-t-2xl transition-colors duration-300 ${iosFontStack} max-w-full overflow-hidden flex-shrink-0`}
      style={{
        boxShadow: '0 -4px 24px 0 rgba(0,0,0,0.08)',
        paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))',
      }}
      aria-label="Mobile bottom navigation"
    >
      {/* Search bar placeholder – same flex and min-width as real Autocomplete container */}
      <div className="flex-1 min-w-0" aria-hidden>
        <div
          className="pl-11 pr-10 py-3 w-full text-base bg-white/90 dark:bg-gray-800/90 border border-gray-200/50 dark:border-gray-700/50 rounded-3xl text-gray-400 dark:text-gray-500"
          role="presentation"
        >
          {staticStrings.searchPlaceholder}
        </div>
      </div>
      {/* All Filters button – outline funnel, same size as real (static placeholder) */}
      <div
        className="flex items-center justify-center flex-none rounded-full p-3 font-bold border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm"
        aria-label={staticStrings.allFiltersAria}
        role="presentation"
      >
        <FunnelIconOutline className="w-6 h-6 text-gray-500 dark:text-gray-400" />
      </div>
      {/* Menu button – same size as real (static placeholder) */}
      <div
        className="flex items-center justify-center flex-none rounded-full p-3 font-bold border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm"
        aria-label={staticStrings.menuLabel}
        role="presentation"
      >
        <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
      </div>
    </nav>
  );
}
